import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft, X, Plus, Loader2, CheckCircle2, XCircle, Circle,
  AlertCircle, Calendar, Users, GraduationCap, MapPin,
  Building2,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { organizationService } from '../../services';
import useEmailCheck from '../../hooks/useEmailCheck';

/**
 * Add Organization Page
 * Form to create a new organization in the system
 */

// Shared chrome — kept in lockstep with AddUserPage so the two forms
// read as a single design system.
const CARD_BASE =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04)]';
const CARD_FORM =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.08)] overflow-hidden';

// Saturated avatar palette for the live preview only — this is the one
// place on the form where colour helps you see your work taking shape.
const PREVIEW_AVATAR_GRADIENTS = [
  'from-primary-500 to-primary-700',
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-violet-500 to-purple-600',
  'from-amber-500 to-orange-600',
  'from-cyan-500 to-sky-600',
];
const seededPreviewGradient = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) & 0x7fffffff;
  return PREVIEW_AVATAR_GRADIENTS[hash % PREVIEW_AVATAR_GRADIENTS.length];
};

// 4-segment password strength — purely client-side, no library.
const passwordStrength = (pw = '') => {
  let s = 0;
  if (pw.length >= 8) s += 1;
  if (/[A-Z]/.test(pw)) s += 1;
  if (/[0-9]/.test(pw)) s += 1;
  if (/[^A-Za-z0-9]/.test(pw)) s += 1;
  return s;
};
const STRENGTH_TONE = {
  1: { bg: 'bg-rose-500',    text: 'text-rose-600 dark:text-rose-400',       label: 'Weak' },
  2: { bg: 'bg-amber-500',   text: 'text-amber-600 dark:text-amber-400',     label: 'Fair' },
  3: { bg: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', label: 'Strong' },
  4: { bg: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-300', label: 'Excellent' },
};

// Plan data — same shape as before, reused for both the picker AND the
// live preview chip so they never disagree.
const PLANS = [
  { id: 'free',                name: 'Free Plan',          price: '₹0',     period: 'forever', desc: '3 sessions (one-time), 30 participants', features: ['3 sessions', '30 participants', '1 trainer'] },
  { id: 'pro',                 name: 'Pro Plan',           price: '₹1,999', period: '/month',  desc: '20 sessions/month, 30 participants, 1 trainer', features: ['20 sessions/mo', '30 participants', '1 trainer'] },
  { id: 'pro_plus',            name: 'Pro Plus',           price: '₹5,999', period: '/month',  desc: 'Unlimited sessions, 100 participants, 3 trainers', features: ['Unlimited', '100 participants', '3 trainers'], popular: true },
  { id: 'enterprise_starter',  name: 'Enterprise Starter', price: '₹1.8L',  period: '/year',   desc: 'Unlimited sessions, 200 participants, 5 trainers', features: ['Unlimited', '200 participants', '5 trainers'] },
  { id: 'enterprise_scale',    name: 'Enterprise Scale',   price: 'Custom', period: '',        desc: 'Custom limits, unlimited trainers, dedicated support', features: ['Unlimited', 'Unlimited participants', 'Custom'] },
];

// Plan chip for the live preview (and could be reused on the dashboard).
const PLAN_PILL = {
  free:               'bg-neutral-50 text-neutral-700 ring-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-700',
  pro:                'bg-primary-50 text-primary-700 ring-primary-200 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-500/30',
  pro_plus:           'bg-cyan-50 text-cyan-700 ring-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-300 dark:ring-cyan-500/30',
  enterprise_starter: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30',
  enterprise_scale:   'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30',
};

// Lookup map for the field-label list inside the error summary banner.
const FIELD_LABELS = {
  name: 'Organization name',
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone number',
  password: 'Password',
  confirmPassword: 'Confirm password',
};

const AddOrganizationPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailCheck = useEmailCheck(organizationService.checkEmail);
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    website: '',
    description: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    password: '',
    confirmPassword: '',
    plan: 'free',
    status: 'active',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (name === 'email') emailCheck.checkEmail(value);
    if (name === 'password') {
      if (value.length > 0 && value.length < 8) {
        setErrors((prev) => ({ ...prev, password: 'Password must be at least 8 characters' }));
      } else {
        setErrors((prev) => ({ ...prev, password: '' }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Organization name is required';
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    else if (emailCheck.available === false) newErrors.email = 'This email is already in use';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^\+?[\d\s\-()]+$/.test(formData.phone)) newErrors.phone = 'Invalid phone number format';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm password';
    else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    setErrors(newErrors);
    return { valid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { valid, errors: validationErrors } = validateForm();
    if (!valid) {
      toast.error('Please fix the form errors');
      // Scroll the form into view, then focus the first errored field.
      // We read from the local validationErrors map (returned synchronously
      // by validateForm) because the equivalent setErrors call hasn't
      // committed to React state yet.
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const firstErrorKey = ['name', 'firstName', 'lastName', 'email', 'phone', 'password', 'confirmPassword']
        .find((k) => !!validationErrors[k]);
      if (firstErrorKey) {
        const node = formRef.current?.querySelector(`[name="${firstErrorKey}"]`);
        node?.focus?.();
      }
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        name: formData.name,
        website: formData.website || undefined,
        description: formData.description || undefined,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country,
        },
        contactPerson: `${formData.firstName} ${formData.lastName}`.trim(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        jobTitle: formData.jobTitle || undefined,
        password: formData.password,
        plan: formData.plan,
        status: formData.status,
      };
      await organizationService.createOrganization(submitData);
      toast.success('Organization created successfully!');
      navigate('/admin/organizations');
    } catch (error) {
      toast.error(error.message || 'Failed to create organization');
      console.error('Error creating organization:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Read-only view of `errors` used by handleSubmit's scroll logic.
  // setErrors hasn't flushed yet at the time we scroll, but we can
  // re-derive what *will* be there by re-running the same checks.
  const errorsAfterValidation = (key) => {
    if (key === 'name' && !formData.name.trim()) return true;
    if (key === 'firstName' && !formData.firstName.trim()) return true;
    if (key === 'lastName' && !formData.lastName.trim()) return true;
    if (key === 'email' && (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) || emailCheck.available === false)) return true;
    if (key === 'phone' && (!formData.phone.trim() || !/^\+?[\d\s\-()]+$/.test(formData.phone))) return true;
    if (key === 'password' && (!formData.password || formData.password.length < 8)) return true;
    if (key === 'confirmPassword' && (!formData.confirmPassword || formData.password !== formData.confirmPassword)) return true;
    return false;
  };

  const handleCancel = () => navigate('/admin/organizations');

  // ⌘+Enter / Ctrl+Enter submits the form when not already submitting.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !isSubmitting) {
        e.preventDefault();
        formRef.current?.requestSubmit?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSubmitting]);

  // Progress checklist — kept simple, derived from the same checks
  // validateForm runs.
  const validEmail = formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && emailCheck.available !== false;
  const sectionDone = {
    org: formData.name.trim() !== '',
    contact: formData.firstName.trim() !== '' && formData.lastName.trim() !== '' && validEmail && formData.password.length >= 8 && formData.password === formData.confirmPassword,
    sub: !!formData.plan && !!formData.status,
  };

  // Live preview values
  const previewInitial = (formData.name || '').charAt(0).toUpperCase();
  const previewGradient = seededPreviewGradient(previewInitial || 'A');
  const selectedPlan = PLANS.find((p) => p.id === formData.plan) || PLANS[0];
  const planPillCls = PLAN_PILL[formData.plan] || PLAN_PILL.free;

  // Password meter
  const pwStrength = passwordStrength(formData.password);
  const pwTone = STRENGTH_TONE[pwStrength];
  const passwordsMatch = formData.confirmPassword.length > 0 && formData.password === formData.confirmPassword;

  // Error summary
  const errorKeys = Object.keys(errors).filter((k) => !!errors[k]);
  const errorLabelList = errorKeys.map((k) => FIELD_LABELS[k] || k).join(', ');

  // Status chip group — same recipe as the list-pages filter chips.
  const STATUS_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' },
  ];

  return (
    <DashboardLayout role="super-admin">
      <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)] gap-6 w-full px-4 lg:px-6 py-6">
        {/* ────── LEFT RAIL ────── */}
        <aside className="lg:sticky lg:top-6 lg:self-start space-y-4">
          {/* A. Page identity */}
          <div className={`${CARD_BASE} p-5`}>
            <span className="inline-flex w-10 h-10 rounded-xl items-center justify-center bg-primary-50 ring-1 ring-inset ring-primary-100 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400 dark:ring-primary-500/20">
              <Building2 size={18} strokeWidth={1.75} />
            </span>
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-50 mt-3">New organization</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Create a new organization and its primary contact.</p>
            <button
              type="button"
              onClick={() => navigate('/admin/organizations')}
              className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 inline-flex items-center gap-1 mt-4 transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.75} />
              Back to Organizations
            </button>
          </div>

          {/* B. Live preview */}
          <div className={`${CARD_BASE} overflow-hidden`}>
            <div className="bg-gradient-to-r from-neutral-50 to-white dark:from-neutral-900 dark:to-neutral-900/60 px-4 py-2 text-[10px] uppercase tracking-[0.08em] font-semibold text-neutral-400 border-b border-neutral-100 dark:border-neutral-800/70">
              Preview
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                {previewInitial ? (
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${previewGradient} flex items-center justify-center text-white font-semibold shadow-sm`}>
                    {previewInitial}
                  </div>
                ) : (
                  <div className="w-11 h-11 rounded-xl border-2 border-dashed border-neutral-200 dark:border-neutral-700 flex items-center justify-center text-neutral-300 dark:text-neutral-600">
                    <Building2 size={18} strokeWidth={1.75} />
                  </div>
                )}
                <div className="min-w-0">
                  <p className={`text-sm font-semibold truncate ${formData.name ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-400'}`}>
                    {formData.name || 'Organization name'}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{formData.email || '—'}</p>
                </div>
              </div>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset ${planPillCls}`}>
                {selectedPlan.name}
              </span>
            </div>
          </div>

          {/* C. Progress */}
          <div className={`${CARD_BASE} p-4`}>
            <p className="text-[11px] uppercase tracking-[0.06em] font-semibold text-neutral-400 mb-2">Progress</p>
            {[
              { key: 'org', label: 'Organization details', done: sectionDone.org },
              { key: 'contact', label: 'Primary contact', done: sectionDone.contact },
              { key: 'sub', label: 'Subscription & status', done: sectionDone.sub },
            ].map(({ key, label, done }) => (
              <div key={key} className="flex items-center gap-2 py-1.5">
                {done
                  ? <CheckCircle2 size={14} className="text-emerald-500" />
                  : <Circle size={14} className="text-neutral-300 dark:text-neutral-600" />}
                <span className={`text-sm ${done ? 'text-neutral-700 dark:text-neutral-200' : 'text-neutral-400'}`}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </aside>

        {/* ────── MAIN FORM ────── */}
        <main>
          <form ref={formRef} onSubmit={handleSubmit} className={CARD_FORM}>
            {/* Header */}
            <div className="px-6 lg:px-8 pt-6 pb-5 border-b border-neutral-100 dark:border-neutral-800/70">
              <h1 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Create organization</h1>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Fill in the details below — required fields are marked with <span className="text-rose-500">*</span>.</p>
            </div>

            <div className="p-6 lg:p-8 space-y-10">
              {/* Error summary */}
              {errorKeys.length > 0 && (
                <div className="rounded-xl bg-rose-50 ring-1 ring-inset ring-rose-200 px-4 py-3 flex items-start gap-3 dark:bg-rose-500/10 dark:ring-rose-500/20">
                  <AlertCircle size={18} strokeWidth={1.75} className="text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-rose-900 dark:text-rose-200">Please fix the errors below</p>
                    <p className="text-xs text-rose-700/80 dark:text-rose-300/80 mt-0.5">{errorLabelList}</p>
                  </div>
                </div>
              )}

              {/* ── Section 1 ── */}
              <section>
                <SectionHeader step="01" title="Organization details" subtitle="Public information about the company." />
                <div className="space-y-4">
                  <Input
                    label="Organization Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="ABC Corporation"
                    required
                    fullWidth
                    size="sm"
                    error={errors.name}
                  />

                  <Input
                    label="Website"
                    name="website"
                    type="url"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://www.example.com"
                    fullWidth
                    size="sm"
                  />

                  <div>
                    <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">
                      Description <span className="text-neutral-400 font-normal">(optional)</span>
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={2}
                      className="w-full px-3 py-2 text-sm rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition resize-none"
                      placeholder="Brief description of the organization"
                    />
                  </div>

                  {/* Address sub-group */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-2 text-xs font-medium text-neutral-700 dark:text-neutral-200">
                      <MapPin size={14} strokeWidth={1.75} className="text-neutral-400" />
                      Address
                      <span className="text-neutral-400 font-normal">(optional)</span>
                    </div>
                    <div className="rounded-xl bg-neutral-50/60 dark:bg-neutral-900/40 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 p-4 space-y-3">
                      <Input name="street" value={formData.street} onChange={handleChange} placeholder="Street Address" fullWidth size="sm" />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Input name="city" value={formData.city} onChange={handleChange} placeholder="City" fullWidth size="sm" />
                        <Input name="state" value={formData.state} onChange={handleChange} placeholder="State/Province" fullWidth size="sm" />
                        <Input name="zipCode" value={formData.zipCode} onChange={handleChange} placeholder="ZIP/Postal Code" fullWidth size="sm" />
                        <Input name="country" value={formData.country} onChange={handleChange} placeholder="Country" fullWidth size="sm" />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <div className="border-t border-neutral-100 dark:border-neutral-800/60" />

              {/* ── Section 2 ── */}
              <section>
                <SectionHeader
                  step="02"
                  title="Primary contact"
                  subtitle="This person will manage the organization and receive login credentials."
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="John" required fullWidth size="sm" error={errors.firstName} />
                  <Input label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Doe" required fullWidth size="sm" error={errors.lastName} />

                  <Input
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john.doe@company.com"
                    required
                    fullWidth
                    size="sm"
                    error={errors.email || emailCheck.error}
                    helperText={!errors.email && !emailCheck.error && emailCheck.available === true ? 'Email is available' : undefined}
                    rightIcon={
                      formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ? (
                        emailCheck.checking ? <Loader2 size={16} className="animate-spin text-neutral-400" /> :
                        emailCheck.available === true ? <CheckCircle2 size={16} className="text-emerald-500" /> :
                        emailCheck.available === false ? <XCircle size={16} className="text-rose-500" /> : null
                      ) : null
                    }
                  />

                  {/* Phone — visual "+" prefix only; state stays untouched */}
                  <div>
                    <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">
                      Phone Number <span aria-label="required" className="ml-0.5 text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm pointer-events-none">+</span>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="91 98765 43210"
                        required
                        className={`w-full pl-7 pr-3 py-2 text-sm rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ${errors.phone ? 'ring-rose-300 focus:ring-rose-500/40' : 'ring-neutral-200 dark:ring-neutral-800 focus:ring-primary-500/30'} text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:outline-none focus:ring-2 transition`}
                      />
                    </div>
                    {errors.phone && <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">{errors.phone}</p>}
                  </div>

                  <Input label="Job Title" name="jobTitle" value={formData.jobTitle} onChange={handleChange} placeholder="Training Manager" fullWidth size="sm" />

                  {/* Password + strength meter */}
                  <div>
                    <Input
                      label="Password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a password"
                      required
                      fullWidth
                      size="sm"
                      error={errors.password}
                    />
                    {formData.password.length > 0 && pwStrength > 0 && (
                      <>
                        <div className="mt-2 flex gap-1">
                          {[0, 1, 2, 3].map((i) => (
                            <span
                              key={i}
                              className={`h-1 flex-1 rounded-full ${i < pwStrength ? pwTone.bg : 'bg-neutral-200 dark:bg-neutral-800'}`}
                            />
                          ))}
                        </div>
                        <p className={`text-[11px] mt-1 ${pwTone.text}`}>{pwTone.label}</p>
                      </>
                    )}
                    {!errors.password && formData.password.length === 0 && (
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Must be at least 8 characters</p>
                    )}
                  </div>

                  {/* Confirm password + match indicator */}
                  <div>
                    <Input
                      label="Confirm Password"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm password"
                      required
                      fullWidth
                      size="sm"
                      error={errors.confirmPassword}
                    />
                    {formData.password.length > 0 && formData.confirmPassword.length > 0 && (
                      <p className={`flex items-center gap-1 text-[11px] mt-1 ${passwordsMatch ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                        {passwordsMatch ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                        {passwordsMatch ? 'Passwords match' : "Passwords don't match"}
                      </p>
                    )}
                  </div>
                </div>
              </section>

              <div className="border-t border-neutral-100 dark:border-neutral-800/60" />

              {/* ── Section 3 ── */}
              <section>
                <SectionHeader step="03" title="Subscription & status" subtitle="Choose a plan and account status." />

                {/* Plan cards */}
                <div className="space-y-2 mb-5">
                  {PLANS.map((plan) => {
                    const selected = formData.plan === plan.id;
                    return (
                      <button
                        type="button"
                        key={plan.id}
                        onClick={() => setFormData((prev) => ({ ...prev, plan: plan.id }))}
                        aria-pressed={selected}
                        className={`w-full text-left rounded-2xl bg-white dark:bg-neutral-900 p-4 cursor-pointer transition-all ${
                          selected
                            ? 'ring-2 ring-primary-500/60 bg-primary-50/40 dark:bg-primary-500/5 shadow-[0_4px_16px_-6px_rgba(255,138,0,0.25)]'
                            : 'ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 hover:ring-neutral-300 dark:hover:ring-neutral-700 hover:shadow-[0_4px_12px_-4px_rgba(16,24,40,0.06)]'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <span
                            aria-hidden="true"
                            className={`w-5 h-5 rounded-full ring-2 flex items-center justify-center flex-shrink-0 ${
                              selected ? 'ring-primary-500' : 'ring-neutral-300 dark:ring-neutral-700'
                            }`}
                          >
                            {selected && <span className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                          </span>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{plan.name}</h3>
                              {plan.popular && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-primary-100 text-primary-700 ring-1 ring-inset ring-primary-200 dark:bg-primary-500/15 dark:text-primary-300 dark:ring-primary-500/30">
                                  Popular
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{plan.desc}</p>
                            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                              <FeatureChip Icon={Calendar} text={plan.features[0]} />
                              <FeatureChip Icon={Users} text={plan.features[1]} />
                              <FeatureChip Icon={GraduationCap} text={plan.features[2]} />
                            </div>
                          </div>

                          <div className="text-right flex-shrink-0">
                            <p className="text-base font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">{plan.price}</p>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400">{plan.period}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Status — segmented control */}
                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-2">
                    Status <span className="text-rose-500">*</span>
                  </label>
                  <div
                    className="inline-flex items-center p-1 rounded-xl bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800"
                    role="group"
                    aria-label="Status"
                  >
                    {STATUS_OPTIONS.map((opt) => {
                      const active = formData.status === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, status: opt.value }))}
                          aria-pressed={active}
                          className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            active
                              ? 'bg-white dark:bg-neutral-900 text-primary-700 dark:text-primary-300 shadow-sm ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700'
                              : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>
            </div>

            {/* Sticky footer */}
            <div className="sticky bottom-0 z-10 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-md border-t border-neutral-100 dark:border-neutral-800/70 px-6 lg:px-8 py-4 flex items-center justify-between gap-3">
              <p className="text-xs text-neutral-500 dark:text-neutral-400 hidden sm:flex items-center gap-1.5">
                <kbd className="inline-flex items-center px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700 text-[10px] font-mono text-neutral-600 dark:text-neutral-300">⌘ Enter</kbd>
                to submit
              </p>
              <div className="flex items-center gap-2 ml-auto">
                <Button variant="ghost" type="button" onClick={handleCancel} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <><Loader2 size={14} className="animate-spin mr-1.5" /> Creating…</>
                  ) : (
                    <><Plus size={14} className="mr-1.5" /> Create Organization</>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </main>
      </div>
    </DashboardLayout>
  );
};

// ────── small subcomponents (file-local) ──────

// Numbered step badge — same recipe as AddUserPage so the two forms read as
// one design system. `step` takes priority; `Icon` stays as a fallback.
const SectionHeader = ({ step, Icon, title, subtitle }) => (
  <div className="flex items-center gap-3 mb-5">
    <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-primary-50 dark:bg-primary-500/10 ring-1 ring-inset ring-primary-100 dark:ring-primary-500/20 text-primary-700 dark:text-primary-300 text-xs font-semibold tracking-wider">
      {step ? step : Icon ? <Icon size={15} strokeWidth={1.75} /> : null}
    </span>
    <div>
      <h2 className="text-[15px] font-semibold text-neutral-900 dark:text-neutral-100 leading-tight">{title}</h2>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{subtitle}</p>
    </div>
  </div>
);

const FeatureChip = ({ Icon, text }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-neutral-100 text-neutral-600 ring-1 ring-inset ring-neutral-200/70 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-700">
    <Icon size={11} strokeWidth={1.75} />
    {text}
  </span>
);

export default AddOrganizationPage;
